import { useEffect, useMemo, useState } from "react"

import { Flex, Space, Spin } from "antd"
import cls from "classnames"
import { t } from "i18next"
import { useDispatch, useSelector } from "react-redux"

import ButtonComponent from "../../../../../Components/Button"
import {
  COMPARE_IDS,
  getCompareParams
} from "../../../../../Components/Filters/CompareSelect/presets"
import FAQModal from "../../../../../Components/Modal/FAQModal"
import Icon from "../../../../../Icon"
import Images from "../../../../../Images"
import { getDates } from "../../../../../Utils/utils"
import { useDemoDataToggle } from "../../../../User/hooks/useDemoDataToggle"
import MetricsRow from "../../../components/Monitoring/MetricsRow/MetricsRow"
import { healthSetProp } from "../../../redux/health.slice"
import { getVampDescriptors, getVampMainChart } from "../../../redux/health.thunks"
import { useVampHistoricalDataMode } from "../../hooks/useHistoricalDataMode"
import { useVampDataAvailable } from "../../hooks/useVampDataAvailable"
import { useVampDescriptor } from "../../hooks/useVampDescriptor"
import DescriptorSelect from "../DescriptorSelect/DescriptorSelect"
import InfoLine from "../InfoLine/InfoLine"
import MainChart from "../MainChart/MainChart"
import SectionHeader from "../SectionHeader/SectionHeader"
import { vampFAQ } from "./vampFAQ"

import "./MainChartBlock.less"

const totalBlocksStyles = {
  vamp_rate: {
    title: "VAMP Rate",
    color: "#35BD29",
    isDashed: true
  },
  fraud_rate: {
    title: "Fraud Rate",
    titleCount: "Fraud Count",
    color: "#225AEA"
  },
  dispute_rate: {
    title: "Dispute Rate",
    titleCount: "Dispute Count",
    color: "#DF7009"
  }
}

const MainChartBlock = ({ filters, business_account_id }) => {
  const dispatch = useDispatch()
  const { isShowDemoData } = useDemoDataToggle()
  const { vampMainChart, vampMainChartFetching, vampDescriptors } = useSelector(
    (state) => state.health
  )
  const descriptor = useVampDescriptor()
  const { isHistoricalDataMode } = useVampHistoricalDataMode()
  const isVampDataAvailable = useVampDataAvailable()
  const { isMobile } = useSelector((state) => state.nav)
  const [dataType, setDataType] = useState("rate")
  const [isFAQOpen, setIsFAQOpen] = useState(false)
  const isComparisonVisible = Boolean(
    filters?.compare?.mode && filters.compare.mode !== COMPARE_IDS.NONE
  )

  useEffect(() => {
    if (filters?.period && isVampDataAvailable) {
      dispatch(
        getVampDescriptors({
          ...getDates(filters.period),
          business_account: business_account_id
        })
      )
    }
  }, [filters?.period, business_account_id, isShowDemoData, isVampDataAvailable])

  useEffect(() => {
    if (filters?.period && descriptor && isVampDataAvailable) {
      const period_query = getDates(filters.period)
      const compare_query = getCompareParams(filters?.compare, period_query)
      dispatch(
        getVampMainChart({
          ...period_query,
          ...compare_query,
          business_account: business_account_id,
          descriptor,
          ...(isHistoricalDataMode ? { historical_data: true } : {})
        })
      )
    }
  }, [
    filters,
    business_account_id,
    isShowDemoData,
    descriptor,
    isHistoricalDataMode,
    isVampDataAvailable
  ])

  const { metricBlocks, isRegionResolved } = useMemo(() => {
    const compBlocks = vampMainChart?.comparison?.blocks_data
    const compLabel = vampMainChart?.comparison_period?.label
    const threshold = vampMainChart?.threshold
    const isRate = dataType === "rate"
    const isRegionResolved = Number(threshold?.rate ?? 0) > 0

    const metric = (key, value, comparisonValue) => ({
      value: isRegionResolved ? value : null,
      comparisonValue,
      comparisonLabel: compLabel,
      isPrivate: true,
      ...totalBlocksStyles[key],
      // Count mode names the metrics Fraud Count / Dispute Count (§1 R8).
      title: (!isRate && totalBlocksStyles[key].titleCount) || totalBlocksStyles[key].title
    })

    const metrics = isRate
      ? [
          metric("vamp_rate", vampMainChart?.blocks_data?.vamp_rate, compBlocks?.vamp_rate),
          metric("fraud_rate", vampMainChart?.blocks_data?.fraud_rate, compBlocks?.fraud_rate),
          metric("dispute_rate", vampMainChart?.blocks_data?.dispute_rate, compBlocks?.dispute_rate)
        ]
      : [
          metric("fraud_rate", vampMainChart?.blocks_data?.fraud_count, compBlocks?.fraud_count),
          metric(
            "dispute_rate",
            vampMainChart?.blocks_data?.dispute_count,
            compBlocks?.dispute_count
          )
        ]

    return { metricBlocks: metrics, isRegionResolved }
  }, [vampMainChart, dataType])

  const updateDescriptor = (newDescriptor) => {
    dispatch(healthSetProp({ key: "descriptor", value: newDescriptor }))
  }

  const infoLineText = `For the best health of your business - we strictly recommend to keep your VAMP rate under the ${
    isRegionResolved ? `${vampMainChart.threshold.rate}%` : "-"
  } line. It will help to keep your business safe.`

  return (
    <div
      className={cls("health-monitor-main-chart-block", {
        "is-mobile": isMobile
      })}
    >
      <Spin spinning={vampMainChartFetching}>
        <SectionHeader
          title="Visa Acquirer Monitoring Program (VAMP)"
          image={Images.visa_3d}
          marginBottom={16}
        />
        <Flex
          gap={20}
          align="flex-start"
          justify="space-between"
          className="main-chart-legend"
          wrap="wrap-reverse"
        >
          <MetricsRow
            metrics={metricBlocks}
            dataType={dataType}
            isComparisonVisible={isComparisonVisible && isRegionResolved}
          />
          <Flex gap={8} className="main-chart-legend__controls">
            <ButtonComponent
              size={"large"}
              icon={<Icon type={"question_help"} />}
              onClick={() => setIsFAQOpen(true)}
            >
              What is VAMP?
            </ButtonComponent>
            <DescriptorSelect
              value={descriptor}
              onChange={updateDescriptor}
              descriptors={vampDescriptors}
              limit={vampMainChart?.threshold?.rate}
            />
            <Space.Compact size={"large"}>
              <ButtonComponent
                className={cls("compact-btn", {
                  active: dataType === "rate"
                })}
                style={{
                  borderRadius: "12px 0 0 12px",
                  minWidth: 66
                }}
                onClick={() => setDataType("rate")}
              >
                {t("Rate")}
              </ButtonComponent>
              <ButtonComponent
                className={cls("compact-btn", {
                  active: dataType === "count"
                })}
                style={{
                  minWidth: 66
                }}
                onClick={() => setDataType("count")}
              >
                {t("Count")}
              </ButtonComponent>
            </Space.Compact>
          </Flex>
        </Flex>
        <MainChart data={vampMainChart} dataType={dataType} />
        <InfoLine text={infoLineText} variant="neutral" style={{ marginTop: "16px" }} />
      </Spin>
      <FAQModal
        open={isFAQOpen}
        close={() => setIsFAQOpen(false)}
        data={vampFAQ}
        title={"Learn more about Visa Acquirer Monitoring Program"}
      />
    </div>
  )
}

export default MainChartBlock
